import axios, { AxiosError, AxiosResponse } from "axios";
import { apiClient } from "@/api/apiClient";
import { API_PATHS } from "@/constants";
import {
  CustomFormBlock,
  PostAnswerParams,
  UploadDocumentParams,
  UploadDocumentResponse,
} from "@/types";
import { CookiesKey } from "@/types/cookies";
import Cookies from "js-cookie";
import * as Sentry from "@sentry/browser";
import { flattenValueToPaths } from "@/utils/helpers";

const QuizAPI = {
  async fetchBlocks(path: string): Promise<CustomFormBlock[]> {
    try {
      const response: AxiosResponse<CustomFormBlock[]> = await apiClient.get(
        path
      );

      return response.data;
    } catch (error) {
      Sentry.captureException(error);
      if (axios.isAxiosError(error)) {
        // Propagate backend-provided status message.
        const statusMessage =
          (error.response?.data as { status?: string })?.status ?? "";
        if (statusMessage) {
          throw new Error(statusMessage);
        }
      }
      throw new Error("Failed to fetch blocks");
    }
  },

  async postAnswer(
    answerData: PostAnswerParams,
    questionOrder?: number
  ): Promise<any> {
    try {
      const response = await apiClient.post(API_PATHS.POST_ANSWER, {
        ...answerData,
      });

      const isFireHeader = (response as any)?.headers?.get?.("X-Is-Fire") ?? "";
      if (String(isFireHeader)) Cookies.set(CookiesKey.IS_FIRE, isFireHeader);

      return response.data;
    } catch (error) {
      Sentry.withScope((scope) => {
        scope.setTag("api", "postAnswer");
        if (!!questionOrder)
          scope.setTag("questionOrder", String(questionOrder));

        scope.setExtra("payload_raw_preview", {
          keys: Object.keys(answerData),
          value: (answerData as any).value,
        });

        scope.setExtra("quizId", answerData.quizId);
        scope.setExtra("questionId", (answerData as any).id);

        scope.setExtra(
          "value_flat",
          flattenValueToPaths((answerData as any).value, {
            maxDepth: 8,
            maxKeys: 200,
            maxArray: 30,
          })
        );

        if (axios.isAxiosError(error)) {
          scope.setExtra("status", error.response?.status);
          scope.setExtra("response_data", error.response?.data);
          scope.setExtra("url", error.config?.url);
          scope.setExtra("method", error.config?.method);
        } else if (error instanceof Error) {
          scope.setExtra("name", error.name);
          scope.setExtra("message", error.message);
          scope.setExtra("stack", error.stack);
        } else {
          scope.setExtra("non_error_thrown", error);
        }

        Sentry.captureException(error);
      });

      throw error;
    }
  },

  // check if email is taken and send phone verification OTP
  async verifyContacts(body: {
    email: string;
    phone_number: string;
  }): Promise<any> {
    try {
      const response = await apiClient.post(API_PATHS.VERIFY_CONTACTS, {
        ...body,
      });

      if (response.status === 200) {
        return "";
      }
    } catch (error) {
      if ((error as AxiosError).response?.status === 409) {
        const respBody = (error as AxiosError).response?.data as any;

        if (respBody?.conflict_property === "phone_number") return "phone";
        if (respBody?.conflict_property) return respBody.conflict_property;

        Sentry.captureException(error);
        throw error;
      }
      Sentry.captureException(error);
      throw error;
    }
  },

  async uploadDocument({
    userId,
    type,
    files,
    isAnonymous = false,
    questionId = "",
    quizId = "",
  }: UploadDocumentParams): Promise<UploadDocumentResponse> {
    const formData = new FormData();

    if (isAnonymous) {
      files.forEach((file, index) => {
        formData.append(`images[${index}]`, file);
      });
      formData.append("value", "true");
      formData.append("id", questionId);
      formData.append("quizId", quizId);
      formData.append("userId", userId || "");
      formData.append("type", String(type));
    } else {
      files.forEach((file, index) => {
        formData.append(`files[${index}][type]`, String(type));
        formData.append(`files[${index}][file]`, file);
      });
      formData.append("order_id", userId || "");
    }

    try {
      const response = await apiClient.post<UploadDocumentResponse>(
        isAnonymous ? API_PATHS.POST_ANSWER : API_PATHS.UPLOAD_DOCUMENT,
        formData
      );
      return response.data;
    } catch (error) {
      const formDebug: any[] = [];
      for (const [key, value] of formData.entries()) {
        if (value instanceof File) {
          formDebug.push({
            key,
            file: { name: value.name, type: value.type, size: value.size },
          });
        } else {
          formDebug.push({ key, value: String(value) });
        }
      }

      Sentry.withScope((scope) => {
        scope.setTag("api", "uploadDocument");
        scope.setExtra("formData", formDebug);
        scope.setExtra("questionId", questionId);

        if (axios.isAxiosError(error)) {
          scope.setExtra("status", error.response?.status);
          scope.setExtra("response_data", error.response?.data);
          scope.setExtra("url", error.config?.url);
          scope.setExtra("method", error.config?.method);
        } else if (error instanceof Error) {
          scope.setExtra("name", error.name);
          scope.setExtra("message", error.message);
          scope.setExtra("stack", error.stack);
        } else {
          scope.setExtra("non_error_thrown", error);
        }

        Sentry.captureException(error);
      });
      throw new Error(
        axios.isAxiosError(error)
          ? error.message
          : `Error uploading document ${error}`
      );
    }
  },
};

export const { fetchBlocks, postAnswer, uploadDocument, verifyContacts } =
  QuizAPI;
